/**
 * ユーザーのアクションログ（操作履歴）の記録ユーティリティ。
 *
 * - 「誰が・いつ・どの機能（対象）に対して・どんな操作（作成/更新/削除/ログイン/ログアウト）をしたか」
 *   だけを記録する。操作した「中身（本文・入力値）」は一切保存しない（プライバシー保護）。
 * - 記録は各操作の成功時に {@link logUserAction} を呼ぶだけ。失敗しても業務処理を止めない（握りつぶす）。
 * - 閲覧は IT開発者フラグ（profiles.is_it_developer）保有者のみ（RLS で保証）。
 */

import { supabase } from '@/lib/supabase';

export type ActionType = 'create' | 'update' | 'delete' | 'login' | 'logout';

export interface LogUserActionInput {
  /** 操作の種類。 */
  actionType: ActionType;
  /** 操作対象の機能キー（例: 'work_progress' / 'announcement' / 'auth'）。 */
  targetType: string;
  /** 対象レコードのID（任意。中身ではないので保存してよい）。 */
  targetId?: string | null;
  /** 対象の事業所ID（任意）。 */
  officeId?: string | null;
  /** 記録者ID。省略時はキャッシュ済み or 現在のセッションのユーザーを使う。 */
  userId?: string;
}

/** 操作種別の表示メタ（一覧ページで使用）。 */
export const ACTION_TYPE_META: Record<
  ActionType,
  { label: string; icon: string; className: string }
> = {
  create: { label: '作成', icon: 'ri-add-line', className: 'bg-emerald-50 text-emerald-700' },
  update: { label: '更新', icon: 'ri-edit-line', className: 'bg-amber-50 text-amber-700' },
  delete: { label: '削除', icon: 'ri-delete-bin-line', className: 'bg-red-50 text-red-700' },
  login: {
    label: 'ログイン',
    icon: 'ri-login-box-line',
    className: 'bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)]',
  },
  logout: { label: 'ログアウト', icon: 'ri-logout-box-line', className: 'bg-gray-100 text-gray-600' },
};

export const getActionTypeMeta = (actionType: string) =>
  ACTION_TYPE_META[actionType as ActionType] ?? ACTION_TYPE_META.update;

/**
 * 対象機能キーの表示ラベル。記録側（各ページ）と一覧側（フィルタ）で共有する。
 * 未知のキーはキー名をそのまま表示する。
 */
export const ACTION_TARGET_LABEL: Record<string, string> = {
  auth: 'ログイン/ログアウト',
  work_task: '作業タスク',
  work_progress: '作業進捗',
  daily_report: '日報',
  attendance: '勤怠',
  announcement: 'お知らせ',
  consultation: '相談',
  manual: 'マニュアル',
  shared_link: 'リンクシェア',
  user: 'ユーザー',
  organization: '組織',
  schedule: 'スケジュール',
  project: 'プロジェクト',
  project_todo: 'プロジェクトTODO',
  business_card: '名刺',
  sales_report: '営業報告',
  message: 'メッセージ',
  it_support: 'ITサポート',
  it_support_reply: 'ITサポート返信',
  it_support_status: 'ITサポート状況変更',
  it_support_assign: 'ITサポート担当',
  it_support_contact: 'ITサポート直接連絡',
  it_support_waiting: 'ITサポート空き待ち',
  leave: '休暇',
  meeting: 'オンライン会議',
  mendan: '面談記録',
};

export const getActionTargetLabel = (targetType: string): string =>
  ACTION_TARGET_LABEL[targetType] ?? targetType;

/** 一覧のフィルタで使う対象キーの選択肢（表示はしている順）。 */
export const ACTION_TARGET_FILTER_OPTIONS: string[] = Object.keys(ACTION_TARGET_LABEL);

// 記録者IDのキャッシュ。プロフィール取得時に AuthContext から設定しておくと getSession の往復を省ける。
let cachedUserId: string | null = null;

/** 現在ログイン中のユーザーIDをキャッシュする（AuthContext から呼ぶ）。 */
export function setActionLogUser(userId: string | null): void {
  cachedUserId = userId;
}

async function resolveUserId(): Promise<string | null> {
  if (cachedUserId) return cachedUserId;
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * アクションログを1件記録する。
 * 記録の失敗は業務処理に影響させないため、例外は投げず警告ログのみ出す。
 */
export async function logUserAction(input: LogUserActionInput): Promise<void> {
  try {
    const userId = input.userId ?? (await resolveUserId());
    if (!userId) return;
    const { error } = await supabase.from('user_action_logs').insert([
      {
        user_id: userId,
        office_id: input.officeId ?? null,
        action_type: input.actionType,
        target_type: input.targetType,
        target_id: input.targetId ?? null,
      },
    ]);
    if (error) {
      console.warn('アクションログの記録に失敗:', error.message);
    }
  } catch (err) {
    console.warn('アクションログの記録に失敗:', err);
  }
}